/**
 * SettingsPage Component
 *
 * Main settings page with tab-based navigation.
 * Tabs:
 * - General: Profile, Appearance, Notifications, Advanced settings
 * - Integrations: GoDaddy credentials and other integrations
 * - Usage: Token usage dashboard (AC-612, AC-613)
 *
 * Technical Design Reference: docs/technical/TD-AC-594-token-audit.md
 * Related Tickets:
 * - AC-613 (Wire UsageDashboard into SettingsPage)
 */

import React, { useState, useEffect } from 'react';
import { Settings, User, Palette, Bell, Plug, Activity, Globe, ArrowRight } from 'lucide-react';
import MCPServerListUserLevel from './MCPServerListUserLevel';
import WhatsAppSettings from './WhatsAppSettings';
import SlackInboundSettings from './SlackInboundSettings';
import ThemeToggle from './ThemeToggle';
import { UsageDashboard } from '../TokenUsage';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../../services/api';
import { Project } from '../../types';

/**
 * Available settings tabs.
 */
type SettingsTab = 'general' | 'integrations' | 'usage';

interface SettingsPageProps {
  initialTab?: SettingsTab;
}

const VALID_TABS: SettingsTab[] = ['general', 'integrations', 'usage'];

const SettingsPage: React.FC<SettingsPageProps> = ({ initialTab = 'general' }) => {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as SettingsTab | null;
  const resolvedInitial = tabParam && VALID_TABS.includes(tabParam) ? tabParam : initialTab;

  // Tab state
  const [activeTab, setActiveTab] = useState<SettingsTab>(resolvedInitial);
  const navigate = useNavigate();

  // Project state for integrations tab redirect notice
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setIsLoadingProjects(true);
      const projectList = await apiService.getProjects();
      setProjects(projectList);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setIsLoadingProjects(false);
    }
  };

  /**
   * Render the General settings tab content.
   */
  const renderGeneralTab = () => (
    <div className="space-y-6">
      {/* Profile Settings */}
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow p-4 sm:p-6">
        <div className="flex items-center mb-4">
          <User className="w-5 h-5 text-gray-600 dark:text-gray-400 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Profile</h2>
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-sm">
          Update your profile information and preferences.
        </p>
      </div>

      {/* Appearance Settings */}
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow p-4 sm:p-6">
        <div className="flex items-center mb-4">
          <Palette className="w-5 h-5 text-gray-600 dark:text-gray-400 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Appearance</h2>
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
          Customize the look and feel of the application.
        </p>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Theme</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Choose light, dark, or system theme</p>
          </div>
          <ThemeToggle />
        </div>
      </div>

      {/* Notification Settings */}
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow p-4 sm:p-6">
        <div className="flex items-center mb-4">
          <Bell className="w-5 h-5 text-gray-600 dark:text-gray-400 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Notifications</h2>
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-sm">
          Configure how you receive notifications.
        </p>
      </div>

      {/* Advanced Settings */}
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow p-4 sm:p-6">
        <div className="flex items-center mb-4">
          <Settings className="w-5 h-5 text-gray-600 dark:text-gray-400 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Advanced</h2>
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-sm">
          Advanced configuration options and preferences.
        </p>
      </div>
    </div>
  );

  /**
   * Render the Integrations tab content.
   *
   * Two-section layout:
   * - "My Integrations" (user-level): always visible, no project required
   * - "Project Integrations" (project-level): requires project selection
   */
  const renderIntegrationsTab = () => (
    <div className="space-y-6">
      {/* Section 1: WhatsApp Integration (user-level) */}
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow p-4 sm:p-6">
        <WhatsAppSettings />
      </div>

      {/* Section 1b: Slack inbound ChatOps (user-level, AC-1819/AC-1823) */}
      <SlackInboundSettings />

      {/* Section 2: My Integrations (user-level) */}
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow p-4 sm:p-6">
        <div className="flex items-center mb-4">
          <Globe data-testid="globe-icon" className="w-5 h-5 text-purple-600 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white">My Integrations</h2>
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
          These MCP servers are available in all your projects.
        </p>
        <MCPServerListUserLevel />
      </div>

      {/* Section 3: Project Integrations - Redirect notice */}
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow p-4 sm:p-6">
        <div className="flex items-center mb-4">
          <Plug data-testid="plug-icon" className="w-5 h-5 text-primary-600 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Project Integrations</h2>
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
          Project-specific integrations (MCP servers, GoDaddy, etc.) are now managed from within each project.
        </p>

        {isLoadingProjects && (
          <div className="animate-pulse">
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
          </div>
        )}

        {!isLoadingProjects && projects.length === 0 && (
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            Create a project to configure project-specific integrations.
          </p>
        )}

        {!isLoadingProjects && projects.length > 0 && (
          <div className="space-y-2">
            {projects.map((project) => (
              <button
                key={project.project_id}
                onClick={() => navigate(`/projects/${project.project_id}/integrations`)}
                className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group"
              >
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{project.name}</span>
                <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-primary-500 transition-colors" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  /**
   * Render the Usage tab content.
   */
  const renderUsageTab = () => (
    <UsageDashboard />
  );

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-warm-white">Settings</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Manage your account, integrations, and usage
        </p>
      </div>

      {/* Desktop Tab Navigation */}
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700 hidden md:block">
        <nav className="-mb-px flex space-x-8" aria-label="Settings tabs">
          <button
            data-testid="tab-general"
            onClick={() => setActiveTab('general')}
            className={`
              py-4 px-1 border-b-2 font-medium text-sm transition-colors
              ${activeTab === 'general'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              }
            `}
          >
            <div className="flex items-center">
              <Settings className="w-4 h-4 mr-2" />
              General
            </div>
          </button>

          <button
            data-testid="tab-integrations"
            data-tutorial="tab-integrations"
            onClick={() => setActiveTab('integrations')}
            className={`
              py-4 px-1 border-b-2 font-medium text-sm transition-colors
              ${activeTab === 'integrations'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              }
            `}
          >
            <div className="flex items-center">
              <Plug className="w-4 h-4 mr-2" />
              Integrations
            </div>
          </button>

          <button
            data-testid="tab-usage"
            onClick={() => setActiveTab('usage')}
            className={`
              py-4 px-1 border-b-2 font-medium text-sm transition-colors
              ${activeTab === 'usage'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              }
            `}
          >
            <div className="flex items-center">
              <Activity className="w-4 h-4 mr-2" />
              Token Usage
            </div>
          </button>
        </nav>
      </div>

      {/* Mobile Tab Navigation - Vertical stacked */}
      <div className="md:hidden flex flex-col gap-1 mb-4" role="tablist" aria-label="Settings tabs">
        <button
          data-testid="tab-general-mobile"
          onClick={() => setActiveTab('general')}
          role="tab"
          aria-selected={activeTab === 'general'}
          className={`w-full text-left px-4 py-3 rounded-lg min-h-[48px] text-sm font-medium transition-colors flex items-center
            ${activeTab === 'general'
              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
            }
          `}
        >
          <Settings className="w-4 h-4 mr-2" />
          General
        </button>
        <button
          data-testid="tab-integrations-mobile"
          onClick={() => setActiveTab('integrations')}
          role="tab"
          aria-selected={activeTab === 'integrations'}
          className={`w-full text-left px-4 py-3 rounded-lg min-h-[48px] text-sm font-medium transition-colors flex items-center
            ${activeTab === 'integrations'
              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
            }
          `}
        >
          <Plug className="w-4 h-4 mr-2" />
          Integrations
        </button>
        <button
          data-testid="tab-usage-mobile"
          onClick={() => setActiveTab('usage')}
          role="tab"
          aria-selected={activeTab === 'usage'}
          className={`w-full text-left px-4 py-3 rounded-lg min-h-[48px] text-sm font-medium transition-colors flex items-center
            ${activeTab === 'usage'
              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
            }
          `}
        >
          <Activity className="w-4 h-4 mr-2" />
          Token Usage
        </button>
      </div>

      {/* Tab Content */}
      <div data-testid="settings-tab-content">
        {activeTab === 'general' && renderGeneralTab()}
        {activeTab === 'integrations' && renderIntegrationsTab()}
        {activeTab === 'usage' && renderUsageTab()}
      </div>
    </div>
  );
};

export default SettingsPage;
